'use client';

import React from 'react';

const JOURNEY_STEPS = [
  {
    id: 'step-discover',
    number: '01',
    label: 'DISCOVER',
    title: 'Self-Assess Your Portfolio',
    desc: 'Use the SMARCK-M guided questionnaire to assess your legacy application portfolio and generate an indicative retirement opportunity verdict.',
    color: '#FF6B6B',
    href: '/discover',
  },
  {
    id: 'step-assess',
    number: '02',
    label: 'ASSESS',
    title: 'Calculate Retirement ROI',
    desc: 'Use the financial calculator to model your retirement program costs, savings, and future maintenance avoided.',
    color: '#F97316',
    href: '/assess',
  },
  {
    id: 'step-report',
    number: '03',
    label: 'REPORT',
    title: 'Export Your Brief',
    desc: 'Generate an Executive Discovery Brief or Assess Report as a PDF for sharing with your leadership team.',
    color: '#F59E0B',
    href: null,
  },
  {
    id: 'step-contact',
    number: '04',
    label: 'CONTACT SMARCK',
    title: 'Share Your Findings',
    desc: 'Share your self-assessment findings with the SMARCK team for expert review and validation.',
    color: '#4ECDC4',
    href: null,
  },
  {
    id: 'step-enterprise-assess',
    number: '05',
    label: 'ENTERPRISE ASSESSMENT',
    title: 'Vendor-Led Discovery',
    desc: 'Receive a detailed, structured discovery engagement led by SMARCK specialists to validate your retirement opportunity.',
    color: '#4ECDC4',
    href: null,
  },
  {
    id: 'step-poc',
    number: '06',
    label: 'PROOF OF CONCEPT',
    title: 'Validate with Real Data',
    desc: 'Execute a proof of concept using your actual application data and systems to demonstrate retirement feasibility and value.',
    color: '#6EE7B7',
    href: null,
  },
  {
    id: 'step-enterprise',
    number: '07',
    label: 'SMARCK ENTERPRISE',
    title: 'Full Platform Deployment',
    desc: 'Deploy the full SMARCK Enterprise Platform for data archiving, compliance management, and application retirement at scale.',
    color: '#6EE7B7',
    href: null,
  },
];

export default function AboutPage() {
  const enterpriseAssessmentMailto =
    'mailto:info@smarck.ai?subject=Enterprise%20Assessment%20Request&body=Hello%20SMARCK%20Team%2C%0A%0AI%20have%20completed%20a%20self-assessment%20using%20SMARCK-M%20and%20would%20like%20to%20request%20an%20Enterprise%20Assessment.%0A%0AOrganization%3A%20%0AIndustry%3A%20%0AApplication%20Portfolio%20Size%3A%20%0A%0APlease%20contact%20me%20to%20discuss%20next%20steps.';

  return (
    <div className="max-w-4xl mx-auto">
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4" style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.2)' }}>
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#FF6B6B' }} />
          <span className="text-xs font-semibold tracking-widest" style={{ color: '#FF6B6B', letterSpacing: '0.1em' }}>
            FREE — NO LOGIN — NO DATA TRANSMITTED
          </span>
        </div>
        <h1 className="text-3xl font-bold text-white mb-3">
          SMARCK-M Enterprise Platform
        </h1>
        <p className="text-base leading-relaxed max-w-2xl mx-auto" style={{ color: '#A8B2C1' }}>
          A free self-assessment tool for enterprise IT leaders evaluating legacy application retirement opportunities. All calculations are performed locally in your browser.
        </p>
      </div>
      {/* ── What is SMARCK-M ──────────────────────────────────────────────── */}
      <div className="rounded-xl p-6 mb-8" style={{ background: '#111F30', border: '1px solid rgba(255,255,255,0.08)' }}>
        <h2 className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: '#4ECDC4', letterSpacing: '0.1em' }}>
          WHAT IS SMARCK-M?
        </h2>
        <div className="flex flex-col gap-4">
          <p className="text-sm leading-relaxed" style={{ color: '#D1D9E3', lineHeight: '1.75' }}>
            SMARCK-M is the web companion to the <strong style={{ color: '#FFFFFF' }}>SMARCK Enterprise Platform</strong> by CLANOMEGA Solutions, Inc. It enables IT leaders, CIOs, and enterprise architects to independently assess their legacy application portfolio — without requiring vendor engagement, login, or data submission.
          </p>
          <p className="text-sm leading-relaxed" style={{ color: '#D1D9E3', lineHeight: '1.75' }}>
            The SMARCK Enterprise Platform is a customer-hosted private AI platform for enterprise data archiving, compliance management, and application retirement. It transforms, validates, and preserves legacy ERP, SaaS, database, and unstructured data into auditable Knowledge Hubs within the customer's own environment — running on-premises or inside the customer's private cloud, with zero dependency on the public internet.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
            {[
              { label: 'All calculations performed', value: 'Locally in your browser' },
              { label: 'Data transmitted', value: 'None — ever' },
              { label: 'Accounts required', value: 'None' },
            ]?.map((item) => (
              <div key={`about-stat-${item?.label}`} className="rounded-lg p-3 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <p className="text-xs mb-1" style={{ color: '#A8B2C1' }}>{item?.label}</p>
                <p className="text-sm font-bold" style={{ color: '#4ECDC4' }}>{item?.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* ── Customer Journey ──────────────────────────────────────────────── */}
      <div className="rounded-xl p-6 mb-8" style={{ background: '#111F30', border: '1px solid rgba(255,255,255,0.08)' }}>
        <h2 className="text-xs font-bold tracking-widest uppercase mb-6" style={{ color: '#4ECDC4', letterSpacing: '0.1em' }}>
          CUSTOMER JOURNEY
        </h2>
        <div className="flex flex-col gap-0">
          {JOURNEY_STEPS?.map((step, idx) => (
            <div key={step?.id} className="flex gap-4">
              {/* Timeline column */}
              <div className="flex flex-col items-center" style={{ width: '40px', flexShrink: 0 }}>
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center font-mono-data text-xs font-bold flex-shrink-0"
                  style={{
                    background: `${step?.color}18`,
                    border: `1.5px solid ${step?.color}50`,
                    color: step?.color,
                  }}
                >
                  {step?.number}
                </div>
                {idx < JOURNEY_STEPS?.length - 1 && (
                  <div className="flex-1 w-0.5 my-1" style={{ background: 'rgba(255,255,255,0.08)', minHeight: '24px' }} />
                )}
              </div>

              {/* Content */}
              <div className="pb-5 flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-bold tracking-widest" style={{ color: step?.color, letterSpacing: '0.08em', fontSize: '9px' }}>
                    {step?.label}
                  </span>
                  {step?.href && (
                    <a
                      href={step?.href}
                      className="text-xs font-medium transition-colors no-underline"
                      style={{ color: '#A8B2C1' }}
                    >
                      → Try it now
                    </a>
                  )}
                </div>
                <p className="text-sm font-semibold text-white mb-1">{step?.title}</p>
                <p className="text-xs leading-relaxed" style={{ color: '#A8B2C1' }}>{step?.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* ── SMARCK Platform Solutions ─────────────────────────────────────── */}
      <div className="rounded-xl p-6 mb-8" style={{ background: '#111F30', border: '1px solid rgba(255,255,255,0.08)' }}>
        <h2 className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: '#4ECDC4', letterSpacing: '0.1em' }}>
          SMARCK ENTERPRISE PLATFORM — SOLUTIONS
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { title: 'Application Retirement', desc: 'Structured decommissioning of legacy applications with data preservation and compliance governance.' },
            { title: 'Data Archival & Retention', desc: 'Long-term, compliant archiving of structured, semi-structured, and unstructured enterprise data.' },
            { title: 'ERP Data Archiving', desc: 'Purpose-built archiving for SAP, Oracle, and other enterprise ERP platforms with full audit trail support.' },
            { title: 'Governance & Compliance', desc: 'Automated retention policy enforcement, legal hold management, and audit-ready evidence packaging.' },
          ]?.map((sol) => (
            <div
              key={`sol-${sol?.title}`}
              className="rounded-lg p-4"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <p className="text-sm font-bold text-white mb-1">{sol?.title}</p>
              <p className="text-xs leading-relaxed" style={{ color: '#A8B2C1' }}>{sol?.desc}</p>
            </div>
          ))}
        </div>
      </div>
      {/* ── Company Info ──────────────────────────────────────────────────── */}
      <div className="rounded-xl p-6 mb-8" style={{ background: '#111F30', border: '1px solid rgba(255,255,255,0.08)' }}>
        <h2 className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: '#4ECDC4', letterSpacing: '0.1em' }}>
          ABOUT THE COMPANY
        </h2>
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.2)' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF6B6B" strokeWidth="1.5">
              <polygon points="9,1 16.5,5 16.5,13 9,17 1.5,13 1.5,5" />
            </svg>
          </div>
          <div>
            <p className="text-base font-bold text-white">CLANOMEGA Solutions, Inc.</p>
            <p className="text-sm mt-0.5" style={{ color: '#4ECDC4' }}>30 N. Gould St., Ste R, Sheridan, WY 82801, USA</p>
            <p className="text-sm mt-2 leading-relaxed" style={{ color: '#A8B2C1' }}>
              Enterprise Data Archiving & Application Retirement Specialists. CLANOMEGA develops the SMARCK Enterprise Platform — a customer-hosted private AI platform for enterprise data governance, compliance archiving, and legacy application retirement. SMARCK operates entirely within the customer's own environment, ensuring data sovereignty, security, and zero dependency on the public internet.
            </p>
          </div>
        </div>
      </div>
      {/* ── Contact & CTA ─────────────────────────────────────────────────── */}
      <div className="rounded-xl p-8 mb-8 text-center" style={{ background: 'rgba(255,107,107,0.06)', border: '1px solid rgba(255,107,107,0.2)' }}>
        <h2 className="text-xl font-bold text-white mb-2">Ready for an Enterprise Assessment?</h2>
        <p className="text-sm mb-6 max-w-xl mx-auto" style={{ color: '#A8B2C1' }}>
          Share your SMARCK-M self-assessment findings with the SMARCK team. We'll review your discovery brief and schedule a structured enterprise assessment engagement.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={enterpriseAssessmentMailto}
            className="btn-primary text-sm px-8 py-3 no-underline"
            style={{ fontSize: '14px', fontWeight: 700 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            Request Enterprise Assessment
          </a>
          <a
            href="mailto:info@smarck.ai"
            className="text-sm font-medium transition-colors no-underline"
            style={{ color: '#4ECDC4' }}
          >
            info@smarck.ai
          </a>
        </div>
      </div>
      {/* ── Privacy Note ──────────────────────────────────────────────────── */}
      <div className="rounded-lg px-4 py-3 mb-6 flex items-start gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ECDC4" strokeWidth="2" className="flex-shrink-0 mt-0.5">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        <p className="text-xs leading-relaxed" style={{ color: '#A8B2C1' }}>
          <strong style={{ color: '#FFFFFF' }}>Privacy:</strong> This tool operates entirely offline. No data is collected, stored, or transmitted. Your assessment data exists only in your browser session and is cleared when you close the tab. The optional business profile is stored in your browser's localStorage only and never transmitted to any server.
        </p>
      </div>
      {/* Footer */}
      <div className="text-center pb-6">
        <p className="text-xs" style={{ color: '#A8B2C1' }}>
          CLANOMEGA Solutions, Inc., 30 N. Gould St., Ste R, Sheridan, WY 82801, USA
        </p>
        <p className="text-xs mt-1" style={{ color: '#A8B2C1' }}>
          <a href="https://smarck.ai" target="_blank" rel="noopener noreferrer" className="transition-colors" style={{ color: '#4ECDC4' }}>
            smarck.ai
          </a>
          {' · '}
          <a href="mailto:info@smarck.ai" className="transition-colors" style={{ color: '#4ECDC4' }}>
            info@smarck.ai
          </a>
        </p>
      </div>
    </div>
  );
}